import type { FormProps } from "~/types/form";
import type {
  InPageFormInput,
  InPageFormOutput,
} from "~/components/Forms/InPageSchema";

import type {
  SubmissionFailed,
  SubmissionSuccessful,
} from "~/components/Forms/api";
import { dispatchInPage, isSubmittedInError } from "~/components/Forms/api";
import {
  hasBeenSubmitted,
  isSubmittedButNotValid,
  isSubmittedAndSuccessful,
} from "~/components/Forms/api";
import {
  createEffect,
  createMemo,
  createSignal,
  DEV,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { useSubmission } from "@solidjs/router";

import { useFormContext } from "~/contexts/FormContext";

import { SharedHiddenFields } from "./Fields/SharedHiddenFields";
import { FirstName } from "~/components/Forms/Fields/FirstName";
import { LastName } from "./Fields/LastName";
import { Email } from "./Fields/Email";
import { Phone } from "./Fields/Phone";
import { Civility } from "./Fields/Civility";
import { Location } from "./Fields/Location";
import { DestinationRadio } from "./Fields/DestinationRadio";

import { DebugDumper } from "./Utils/DebugDumper";
import { useFormStorageContext } from "~/contexts/FormStorageProvider";
import { useEventsContext } from "~/contexts/EventsContext";
import type {
  FormSystemName,
  FormSystemNameWithSubject,
} from "~/types/form_system_name";
import { OptInCogedim } from "./Fields/OptInCogedim";
import { Legals } from "./Utils/Legals";
import { useModalFormContext } from "~/contexts/ModalFormContext";

import IconCheckCircle from "~/img/icons/check_circle.svg";

import { Message } from "./Fields/Message";
import { DateSelect } from "./Fields/DateSelect";
import type { InputOption } from "./Fields/Base/InputTypesProps";
import { formatDateUStoFrench } from "~/utils/format";
import { Hour } from "./Fields/Hour";
import { Errors } from "./Utils/Errors";

import toast from "solid-toast";
import { useCapencyContext } from "~/contexts/CapencyContext";
import { isCapencyResponse } from "./api";
import CapencyReporter from "./Utils/CapencyReporter";
import { DesiredArea } from "./Fields/DesiredArea";
import { shouldShowDesiredArea, shouldShowMessage } from "~/utils/helper_form";

const debug = import.meta.env.VITE_COG_DEBUG_FORMS == "1";

const FORM_NAME: FormSystemName = "in-page";

type Success = SubmissionSuccessful<InPageFormOutput>;

export default function InPage(
  props: FormProps<InPageFormInput> & {
    formNameForEvent: FormSystemNameWithSubject;
    forceDestination?: string;
  },
) {
  const [, { sendShowEvent, sendSubmitEvent }] = useEventsContext();
  const [, { openModalForm, setModalFormHeadline }] = useModalFormContext();
  const [formStore, { setValue, unsetValue }] = useFormContext();
  const [, { getFormValuesFromStorage, setFormValuesToStorage }] =
    useFormStorageContext();

  const [showSent, setShowSent] = createSignal(false);

  const [
    capencyCtx,
    {
      setCapencyLoading,
      setBypassingCapency,
      setCapencyChallengeSucceeded,
      setCapencyShowFailedPanel,
      setEmailMessage,
      setPhoneMessage,
    },
  ] = useCapencyContext();

  const capencyIsDisplayed = () => {
    return (
      capencyCtx.isEnabled &&
      !capencyCtx.isBypassingCapency &&
      (capencyCtx.showFailedPanel || capencyCtx.isLoading)
    );
  };

  const performCapencyCheck = () => {
    const letgo =
      capencyCtx.isCapencyChallengeSucceeded || capencyCtx.isBypassingCapency;
    return capencyCtx.isEnabled && !letgo ? "1" : "0";
  };

  createEffect(() => {
    const v = {
      ...getFormValuesFromStorage(),
      ...props.defaultValues,
      ...props.hiddenFields,
      node_nid: props.formConfig?.parent_id,
    };
    for (const [key, value] of Object.entries(v || {})) {
      setValue!(key, value as string);
    }
  });

  let btn;

  const submission = useSubmission(dispatchInPage);

  const [start, setStart] = createSignal(false);

  setBypassingCapency!(false);
  setCapencyShowFailedPanel!(false);
  setCapencyChallengeSucceeded!(false);

  const dettachFormBehavior = () => {
    if (hasBeenSubmitted(submission)) {
      setBypassingCapency!(false);
      setCapencyShowFailedPanel!(false);
      setCapencyChallengeSucceeded!(false);
      setStart(false);
    }
  };

  createEffect(() => {
    if (start()) {
      // Capency validation went smoothly proceed to submission
      if (isCapencyResponse(submission.result) && submission.result?.valid) {
        setCapencyChallengeSucceeded!(true);
        btn!.click();
      }

      // Capency validation failed
      if (
        isCapencyResponse(submission.result) &&
        submission.result?.valid === false
      ) {
        setCapencyShowFailedPanel!(true);
        setCapencyChallengeSucceeded!(false);
        const ret = submission.result;
        // To avoid the loop effect after re-opening the form upon successful submission
        setEmailMessage!(ret?.data?.email?.sMessage);
        setPhoneMessage!(ret?.data?.phone?.MessageCP?.sMessage);
      }

      // Successful form submission
      if (!isCapencyResponse(submission.result) && submission.result?.success) {
        setCapencyShowFailedPanel!(false);
        sendSubmitEvent({
          crmValues: submission.result.crm.data,
          formValues: formStore.values,
        });
        setFormValuesToStorage((submission.result as Success)?.output);
        dettachFormBehavior();
      }

      // Submission failed
      if (
        !isCapencyResponse(submission.result) &&
        isSubmittedInError(submission)
      ) {
        setCapencyShowFailedPanel!(false);
        console.error((submission.result as SubmissionFailed).issues);
        toast.error("Une erreur est survenue. Merci de réessayer plus tard.", {
          duration: 5000,
        });
      }
    }
  });

  // Auto-submit the form if the user is bypassing Capency
  createEffect(() => {
    if (capencyCtx.isBypassingCapency) {
      DEV && console.log("Bypassing Capency");
      btn!.click();
    }
  });

  // Show the Capency loading panel
  createEffect(() => {
    setCapencyLoading!(
      Boolean(submission.pending) && !capencyCtx.isBypassingCapency,
    );
  });

  // Housekeeping
  onMount(() => {
    if (hasBeenSubmitted(submission)) {
      submission.clear();
      setBypassingCapency!(false);
      setCapencyShowFailedPanel!(false);
      setCapencyChallengeSucceeded!(false);
    }
  });

  // Housekeeping
  onCleanup(() => {
    dettachFormBehavior();
  });

  const dateOptions = createMemo(() => {
    if (props.formConfig?.field_dates) {
      return props.formConfig?.field_dates?.map(
        (dateParagraph) =>
          ({
            label: formatDateUStoFrench(dateParagraph.field_date),
            value: formatDateUStoFrench(dateParagraph.field_date),
            name: dateParagraph.field_date,
          }) as InputOption,
      );
    }

    return [];
  });

  const hourOptions = createMemo(() => {
    if (props.formConfig?.field_dates) {
      return (
        props.formConfig?.field_dates
          ?.find((para) => {
            return (
              formatDateUStoFrench(para.field_date) ===
              formStore.values.date_dedupe
            );
          })
          ?.field_time_slot?.map((slot) => ({
            label: slot,
            value: slot,
            name: slot,
          })) || []
      );
    }

    return [];
  });

  createEffect(() => {
    // If the date changes, we reset the hour, because slot are different.
    formStore.values.date && unsetValue!("hour");
  });

  return (
    <>
      <Show when={start() && capencyIsDisplayed()}>
        <CapencyReporter
          isLoading={capencyCtx.isLoading}
          showFailedPanel={capencyCtx.showFailedPanel}
          email={formStore.values.email}
          phone={formStore.values.phone}
          emailMessage={capencyCtx.emailMessage}
          phoneMessage={capencyCtx.phoneMessage}
        />
      </Show>
      <Show when={isSubmittedButNotValid(submission)}>
        <Errors issues={(submission.result as SubmissionFailed).issues} />
      </Show>

      {/* Form */}
      <Show
        when={
          !hasBeenSubmitted(submission) || isSubmittedButNotValid(submission)
        }
      >
        <form
          action={dispatchInPage}
          method="post"
          onClick={(e) => {
            if (!showSent()) {
              sendShowEvent(props.formNameForEvent, e);
              setShowSent(true);
            }
          }}
          style={{
            display: capencyIsDisplayed() ? "none" : "block",
          }}
        >
          <SharedHiddenFields
            form_id={FORM_NAME}
            performCapencyCheck={performCapencyCheck()}
            defaultValues={{
              ...props.defaultValues,
              ...props.hiddenFields,
              node_nid: props.formConfig?.parent_id,
            }}
          />
          <Show when={props.formConfig?.field_disclaimer}>
            <p class="introduction">{props.formConfig?.field_disclaimer}</p>
          </Show>
          <Show
            when={
              props.formConfig?.field_dates &&
              props.formConfig?.field_dates.length > 0
            }
          >
            {/*

            Some fields are duplicated with a _dedupe suffix
            Because the form storage is a singleton, and as we have multiple forms
            on the same page, we need to deduplicate the fields.

            The `_dedupe` will be removed when the form is submitted, in forms/server.ts
            */}

            <DateSelect
              name="date_dedupe"
              value={formStore.values.date_dedupe}
              options={dateOptions()!}
            />

            <Hour
              name="hour_dedupe"
              value={formStore.values.hour_dedupe}
              options={hourOptions()}
              disabled={hourOptions()!.length === 0}
            />
          </Show>
          <Show when={shouldShowMessage(props.formConfig)}>
            <Message name="message" value={formStore.values.message} />
          </Show>
          <Civility name="civility" value={formStore.values.civility} />
          <FirstName name="first_name" value={formStore.values.first_name} />
          <LastName name="last_name" value={formStore.values.last_name} />
          <Phone name="phone" value={formStore.values.phone} />
          <Email name="email" value={formStore.values.email} />
          <Location name="location" value={formStore.values.location} />
          <Show
            when={props.forceDestination}
            fallback={
              <DestinationRadio
                name="destination"
                value={formStore.values.destination}
                form_name={FORM_NAME}
                wrapper={props.wrapper}
                landing={props.landing}
              />
            }
          >
            <input
              type="hidden"
              name="destination"
              value={props.forceDestination}
            />
          </Show>
          <Show when={shouldShowDesiredArea(props.wrapper || props.formConfig)}>
            <DesiredArea
              name="desired_area"
              value={formStore.values.desired_area}
              required
            />
          </Show>
          <div class="clear" />
          <OptInCogedim name="optin_cogedim" showBrands />
          <button
            ref={btn}
            type="submit"
            class="btn form-submit"
            data-test="cta-submit"
            disabled={submission.pending}
            onClick={() => {
              setStart(true);
            }}
          >
            Envoyer
          </button>
          <Legals function="d'information" />
          <Show when={submission.pending}>
            <p style={{ color: "blue" }} role="alert">
              <em>Envoi en cours…</em>
            </p>
          </Show>
        </form>
      </Show>

      {/* Confirmation */}
      <Show when={isSubmittedAndSuccessful(submission)}>
        <div class="confirmation">
          <i aria-hidden="true" class="cog-icon">
            <IconCheckCircle />
          </i>
          <Show when={props.formConfig?.field_confirmation}>
            <p>{props.formConfig?.field_confirmation}</p>
          </Show>
          <p>
            Si vous le souhaitez, vous pouvez nous aider à préparer notre appel
            en précisant votre besoin.
          </p>
          <button
            type="button"
            class="btn"
            data-test="cta-qualification"
            onClick={(e) => {
              sendShowEvent(`qualification`, e);
              openModalForm!("qualification", {
                wrapper: props.wrapper,
                landing: props.landing,
                defaultValues: {
                  submission_id: submission.result?.crm?.data?.iddemande,
                },
              });
              setModalFormHeadline!("Préciser votre besoin");
            }}
          >
            Préciser mon besoin
          </button>
        </div>
      </Show>

      <Show when={debug} keyed>
        <DebugDumper
          name={FORM_NAME}
          data={{ store: formStore, result: submission.result }}
        />
      </Show>
    </>
  );
}
